'use client';

import { PhoneIcon } from '@heroicons/react/24/solid';
import { useTranslation } from 'next-i18next';
import { clsx } from 'clsx';
import { Button } from '@components/common';
import { RecruiterCallSoftphoneModal } from './RecruiterCallSoftphoneModal';
import { useRecruiterCallEntry } from './useRecruiterCallEntry';

interface CallCandidateActionProps {
  candidateName: string;
  candidateUserId: number;
  jobSlug?: string | null;
  /** Extra classes for the outer relative wrapper (spacing/positioning next to sibling buttons). */
  className?: string;
}

/**
 * Toolbar variant of the RecruiterCall entry point. Sits between "Contactează"
 * and "Shortlist" in the applicant actions bar and follows the same visual
 * pattern:
 *   - green pill badge floating above the button with the price OR the
 *     remaining-free-calls count (whichever applies),
 *   - button hidden entirely when the backend's `canCall` check returns false.
 *
 * The click always opens the softphone modal (which owns the confirm
 * step + the WebRTC softphone). Zero-price still opens the modal — it
 * just labels the call as free instead of showing a credit count.
 */
export const CallCandidateAction = ({
  candidateName,
  candidateUserId,
  jobSlug,
  className,
}: CallCandidateActionProps) => {
  const { t } = useTranslation('common');
  const {
    visible,
    priceCredits,
    remainingFreeCalls,
    isModalOpen,
    openModal,
    closeModal,
  } = useRecruiterCallEntry({ candidateUserId, jobSlug });

  if (!visible) {
    return null;
  }

  const isFree = priceCredits === 0;
  const showRemainingFreeCallsBadge = isFree && remainingFreeCalls !== null;
  const labelKey = isFree ? 'cv.detail.action.free-call.label' : 'cv.detail.action.call.label';
  const label = t(labelKey, { defaultValue: isFree ? 'Sună gratuit' : 'Sună' });

  return (
    <>
      <div className={clsx('relative inline-block', className)}>
        {!isFree && (
          <div className="absolute -top-3.5 left-1/2 z-10 min-w-max -translate-x-1/2 whitespace-nowrap rounded-lg bg-secondary px-1 text-pico font-semibold text-surface">
            {`${t('cv.detail.action.call.price_label')}: `}
            <span className="ml-1">
              {priceCredits} {t('global.label.credits')}
            </span>
          </div>
        )}
        {showRemainingFreeCallsBadge && (
          <div className="absolute -top-3.5 left-1/2 z-10 min-w-max -translate-x-1/2 whitespace-nowrap rounded-lg bg-secondary px-1 text-pico font-semibold text-surface">
            {t('cv.detail.action.call.remaining_free_label', {
              count: remainingFreeCalls,
              defaultValue: '{{count}} free calls left',
            })}
          </div>
        )}
        <Button
          type="button"
          styling="none"
          onClick={openModal}
          className="relative mx-1 flex min-w-32 cursor-pointer items-center justify-center gap-2 rounded-2xl bg-surface-100 px-4.5 py-3.5 text-md font-medium text-ink transition-all duration-200 hover:bg-surface-200 hover:shadow-md"
          title={label}
        >
          <PhoneIcon className="mr-2 size-4" />
          {label}
        </Button>
      </div>

      {isModalOpen && (
        <RecruiterCallSoftphoneModal
          open={isModalOpen}
          candidateName={candidateName}
          candidateUserId={candidateUserId}
          jobSlug={jobSlug ?? null}
          priceCredits={priceCredits}
          onClose={closeModal}
        />
      )}
    </>
  );
};
